import React from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import { isOptionEqualToValue } from 'feature/dispatching/utils';
import {
  useGetLoadingControlsBySupplierQuery,
  useGetSuppliersByProductQuery,
} from '../../loadsEndpoints';
import LoadingTextField from './LoadingTextField';

const ProductForm = ({ compartment, product, loadState, setLoadState, setValidationState }) => {
  const { location } = loadState;
  const { compartmentId } = compartment;
  const { productId, productGradeCode, supplier, loadingControl } = product;

  const compartmentLens = R.lensPath(['compartments', compartmentId]);
  const productLens = R.compose(compartmentLens, R.lensPath(['products', productId]));
  const supplierLens = R.compose(productLens, R.lensProp('supplier'));
  const loadingControlLens = R.compose(productLens, R.lensProp('loadingControl'));
  const additivesLens = R.compose(compartmentLens, R.lensProp('additives'));
  const destinationLens = R.compose(compartmentLens, R.lensProp('destination'));
  const volumeLens = R.compose(compartmentLens, R.lensProp('volume'));

  const {
    data: suppliersQuery = [],
    isFetching: isFetchingSuppliers,
    isSuccess: isSuppliersSuccess,
  } = useGetSuppliersByProductQuery({
    locationId: R.prop('locationId', location),
    productGradeCode,
  });

  const {
    data: loadingControlsQuery = [],
    isFetching: isFetchingLoadingControls,
    isSuccess: isLoadingControlsSuccess,
  } = useGetLoadingControlsBySupplierQuery(
    {
      locationId: R.prop('locationId', location),
      productGradeCode,
      productId,
      supplierId: R.prop('supplierId', supplier),
    },
    {
      skip: R.isNil(supplier),
    }
  );

  const handleSupplierChange = (_, value) => {
    const updateSupplier = R.compose(
      R.set(additivesLens, []),
      R.set(loadingControlLens, null),
      R.set(supplierLens, value),
      R.set(volumeLens, ''),
      R.set(destinationLens, null)
    );
    setLoadState(updateSupplier);
    setValidationState();
  };

  const handleLoadingControlChange = (_, value) => {
    // prettier-ignore
    const updateLcn = R.compose(
      R.set(destinationLens, null),
      R.set(loadingControlLens, value)
    );
    setLoadState(updateLcn);
  };

  return (
    <>
      <Autocomplete
        disableClearable
        disabled={!isSuppliersSuccess}
        getOptionLabel={R.o(R.toString, R.prop('supplierId'))}
        isOptionEqualToValue={isOptionEqualToValue('supplierId')}
        loading={isFetchingSuppliers}
        onChange={handleSupplierChange}
        options={suppliersQuery}
        renderInput={params => (
          <LoadingTextField
            label={`${productGradeCode} Grade Supplier`}
            loading={isFetchingSuppliers}
            params={params}
          />
        )}
        value={supplier}
      />

      {product.supplier?.hasLoadingControls && (
        <Autocomplete
          disableClearable
          disabled={!isLoadingControlsSuccess}
          getOptionLabel={R.prop('loadingControlNumber')}
          isOptionEqualToValue={isOptionEqualToValue('loadingControlId')}
          loading={isFetchingLoadingControls}
          onChange={handleLoadingControlChange}
          options={loadingControlsQuery}
          renderInput={params => (
            <LoadingTextField
              label={`${productGradeCode} Grade Supplier LCN`}
              loading={isFetchingLoadingControls}
              params={params}
            />
          )}
          value={loadingControl}
        />
      )}
    </>
  );
};

ProductForm.propTypes = {
  compartment: PropTypes.shape({
    compartmentId: PropTypes.number,
  }).isRequired,
  loadState: PropTypes.shape({
    location: PropTypes.shape({}),
  }).isRequired,
  product: PropTypes.shape({
    loadingControl: PropTypes.shape({}),
    productGradeCode: PropTypes.string,
    productId: PropTypes.string,
    supplier: PropTypes.shape({
      hasLoadingControls: PropTypes.bool,
    }),
  }).isRequired,
  setLoadState: PropTypes.func.isRequired,
  setValidationState: PropTypes.func.isRequired,
};

export default ProductForm;
